import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import ChatPage from 'supportAssistant/pages/ChatPage'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import SocketContextProvider from 'supportAssistant/contexts/SocketContext'
import { useHistory, useLocation } from 'react-router'
import { connect } from 'react-redux'
import { setOptimizelyExperiment } from 'actions/SystemActions'
import { OptimizelyServerSideExperiments } from 'lib/optimizely/optimizelyUtils'
import { useAppDispatch } from 'hooks/reduxHooks'

const Frame = styled.iframe`
  overflow-y: auto;
  min-height: 1200px;
  -webkit-overflow-scrolling: touch;

  ${mediaQueryUp.tablet} {
    min-height: 1650px;
  }

  ${mediaQueryUp.desktop} {
    min-height: 1100px;
  }

  ${mediaQueryUp.largeDesktop} {
    min-height: 1200px;
  }
`

interface IFrame extends HTMLIFrameElement {
  node: HTMLIFrameElement;
}

interface Props {
  supportAssistantEnabled: boolean;
}

function SupportPage({
  supportAssistantEnabled,
}: Props) {
  const [height, setHeight] = useState(1200)
  const [iframeKey, setIframeKey] = useState(0)
  const iframeRef: React.RefObject<IFrame> = React.useRef()

  const history = useHistory()
  const location = useLocation()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const isDevMode = queryParams.get('devMode')
  const faq = queryParams.get('faq')

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setOptimizelyExperiment(OptimizelyServerSideExperiments.isSupportAssistantEnabled))
  }, [dispatch])

  useEffect(() => {
    // sets random iframeKey when we point to support url without faq query param
    // sets faq query param so when we click on navbar support link we go to /support page without faq query param
    // which will trigger useEffect and set random iframeKey to reset iframe state
    if (!faq && !(supportAssistantEnabled || isDevMode)) {
      setIframeKey(Math.random())
      queryParams.set('faq', '1')
      history.replace({
        search: queryParams.toString(),
      })
    }
  }, [faq, history, queryParams, supportAssistantEnabled, isDevMode])
  // See https://github.com/lux-group/www-le-customer/pull/4838#discussion_r597384790 for an explanation why we need useEffect above

  useEffect(() => {
    const handleResize = (size: number) => {
      if (size !== 0) {
        setHeight(size)
      }
    }

    const resizeMessageHandler = async(event) => {
      const data = typeof event?.data === 'string' && JSON.parse(event?.data)
      if (data?.iframeResize) {
        handleResize(data.message)
      }
    }

    window.addEventListener('message', resizeMessageHandler)
    return () => window.removeEventListener('message', resizeMessageHandler)
  }, [])

  if (supportAssistantEnabled || isDevMode) {
    return <SocketContextProvider>
      <ChatPage />
    </SocketContextProvider>
  }

  return (
    <Frame
      title="support"
      frameBorder="0"
      src={config.supportUrl}
      ref={iframeRef}
      height={height + 'px'}
      key={iframeKey}
    />
  )
}

function mapStateToProps(state: App.State) {
  return {
    supportAssistantEnabled: isSupportAssistantEnabled(state),
  }
}

export default connect(mapStateToProps)(SupportPage)
